import { Collapse } from '@sgme/ui';
import classNames from 'classnames';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { NonUniqueSectionItem } from 'components/common-form/sidePanel/info/QuoteInfos/GreeksFeesMetricsTabContent/NonUniqueSectionItem';
import { UniqueSectionItem } from 'components/common-form/sidePanel/info/QuoteInfos/GreeksFeesMetricsTabContent/UniqueSectionItem';
import { FormattedValue } from 'components/shared/ReadOnlyGrid/FormattedValue';
import { formatGridToClipboard } from 'components/shared/ReadOnlyGrid/GridUtils';
import type { ReadOnlyGridData } from 'components/shared/ReadOnlyGrid/ReadOnlyGrid';
import { ConnectedFees } from 'components/shared/Sensitivity/ConnectedFees';
import { ConnectedMetrics } from 'components/shared/Sensitivity/ConnectedMetrics';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCopyToExcel } from 'hooks/useCopyToExcel';
import type { IntlKey } from 'locales';
import { upperFirst } from 'lodash-es';
import type { FeesAndMetricsFieldMetadata } from 'models/forms/fields';
import { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  batchFeesAndMetricsDataSetChanged,
  batchFeesAndMetricsExpansionToggled,
} from 'state/batchForm/batchForm.action';
import { getBatchFeesAndMetricsDataSet, getBatchFeesAndMetricsIsExpanded } from 'state/batchForm/batchForm.selectors';
import type { Fee, Metric } from 'state/quotes/details/details.model';
import { getFormQuoteDetails } from 'state/selectors/common/commonForm';
import { hasFees, hasMetrics } from 'state/selectors/meta/quote/utils';
import {
  getUserPreferencesFeesPrecision,
  getUserPreferencesMetricsPrecision,
} from 'state/userPreferences/userPreferences.selectors';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

type SensitivityValue = Fee | Metric;

function buildClipboardGrid(data: readonly SensitivityValue[], sectionName: string): ReadOnlyGridData {
  return {
    columns: [
      { name: 'name', alias: sectionName },
      { name: 'value', alias: 'Value' },
    ],
    cells: data,
  };
}

function FeesAndMetricsModalContent({ formId }: { formId: string }) {
  const hasFeesData = useAppSelector(state => hasFees(state, 'newBatch', formId));
  const hasMetricsData = useAppSelector(state => hasMetrics(state, 'newBatch', formId));
  const [activeSections, setActiveSections] = useState<string[]>([]);

  useEffect(() => {
    const nextSections = [
      ...(hasFeesData ? (['fees'] as string[]) : []),
      ...(hasMetricsData ? (['metrics'] as string[]) : []),
    ];

    setActiveSections(current => {
      const filteredCurrent = current.filter(section => nextSections.includes(section));
      return filteredCurrent.length > 0 ? filteredCurrent : nextSections;
    });
  }, [hasFeesData, hasMetricsData]);

  const toggleSection = (section: string) => {
    setActiveSections(current => {
      return current.includes(section) ? current.filter(item => item !== section) : [...current, section];
    });
  };

  const getTitle = (key: string, formatMsg: (id: IntlKey) => string) => {
    return formatMsg(`quoteDetails.Tab.${upperFirst(key)}` as IntlKey);
  };

  const hasOnlyOneSection = Number(hasFeesData) + Number(hasMetricsData) <= 1;

  return (
    <Collapse
      activeKey={activeSections}
      onSelect={key => toggleSection(key as string)}
      fill="fill"
      icon="end"
      size="sm"
      className="overflow-y-auto px-2"
    >
      {hasFeesData && hasOnlyOneSection && (
        <UniqueSectionItem
          getTitle={getTitle}
          content={<ConnectedFees formId={formId} workspace="newBatch" />}
          sectionName="fees"
        />
      )}
      {hasFeesData && !hasOnlyOneSection && (
        <NonUniqueSectionItem getTitle={getTitle} sectionName="fees" noModal>
          <ConnectedFees formId={formId} workspace="newBatch" />
        </NonUniqueSectionItem>
      )}
      {hasMetricsData && hasOnlyOneSection && (
        <UniqueSectionItem
          getTitle={getTitle}
          content={<ConnectedMetrics formId={formId} workspace="newBatch" />}
          sectionName="metrics"
        />
      )}
      {hasMetricsData && !hasOnlyOneSection && (
        <NonUniqueSectionItem getTitle={getTitle} sectionName="metrics" noModal>
          <ConnectedMetrics formId={formId} workspace="newBatch" />
        </NonUniqueSectionItem>
      )}
    </Collapse>
  );
}

export function FeesAndMetricsField({ node: { id }, tabId }: ProductNodeBuilderProps) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const metadata = useFieldMetadata<FeesAndMetricsFieldMetadata>(id);
  const isExpanded = useAppSelector(getBatchFeesAndMetricsIsExpanded);
  const activeDataSet = useAppSelector(getBatchFeesAndMetricsDataSet);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const quoteDetails = useAppSelector(state => getFormQuoteDetails(state, 'newBatch', tabId));
  const hasFeesData = useAppSelector(state => hasFees(state, 'newBatch', tabId));
  const hasMetricsData = useAppSelector(state => hasMetrics(state, 'newBatch', tabId));
  const feesPrecision = useAppSelector(getUserPreferencesFeesPrecision) ?? 4;
  const metricsPrecision = useAppSelector(getUserPreferencesMetricsPrecision) ?? 4;
  const hasData = hasFeesData || hasMetricsData;
  const isExpandable = metadata?.isExpandable === true;
  const isContentExpanded = !isExpandable || isExpanded;
  const fees = getQuoteFeesFromDetails(quoteDetails?.Fees);
  const metrics = quoteDetails?.Metrics;
  const currentDataSet: readonly SensitivityValue[] = activeDataSet === 'fees' ? (fees ?? []) : (metrics ?? []);
  const currentPrecision = activeDataSet === 'fees' ? feesPrecision : metricsPrecision;
  const currentSectionLabel =
    activeDataSet === 'fees'
      ? formatMessage({ id: 'quoteDetails.Tab.Fees' })
      : formatMessage({ id: 'quoteDetails.Tab.Metrics' });

  useEffect(() => {
    if (!hasData) {
      setIsModalOpen(false);
    }
  }, [hasData]);

  const generateClipboardGrid = () => {
    return formatGridToClipboard(buildClipboardGrid(currentDataSet, currentSectionLabel));
  };

  const onCopyToClipboard = useCopyToExcel(generateClipboardGrid);

  const toggleModal = () => {
    setIsModalOpen(current => !current);
  };

  const toggleExpanded = () => {
    dispatch(batchFeesAndMetricsExpansionToggled());
  };

  const setActiveDataSet = (dataSet: 'fees' | 'metrics') => {
    dispatch(batchFeesAndMetricsDataSetChanged(dataSet));
  };

  if (!hasData) {
    return <div className="h-100" data-e2e={`fees-and-metrics-cell-${tabId}`} />;
  }

  if (!isContentExpanded) {
    return (
      <div className="h-100" data-e2e={`fees-and-metrics-cell-${tabId}`}>
        <button
          className="btn btn-flat-secondary d-flex align-items-center justify-content-between w-100 h-100 px-2"
          onClick={toggleExpanded}
          type="button"
        >
          <span className="text-start">
            <FormattedMessage id="form.FeesAndMetrics.view" defaultMessage="View fees & metrics" />
          </span>
          <i aria-hidden="true" className="icon icon-outlined icon-sm">
            unfold_more
          </i>
        </button>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100" data-e2e={`fees-and-metrics-cell-${tabId}`}>
      <div className="d-flex align-items-center gap-2 ms-2">
        <button
          className={classNames(
            'btn btn-link px-0',
            activeDataSet === 'fees' ? 'text-primary text-decoration-underline' : 'text-secondary text-decoration-none',
          )}
          disabled={!hasFeesData}
          onClick={() => setActiveDataSet('fees')}
          type="button"
        >
          <FormattedMessage id="quoteDetails.Tab.Fees" />
        </button>
        <button
          className={classNames(
            'btn btn-link px-0',
            activeDataSet === 'metrics'
              ? 'text-primary text-decoration-underline'
              : 'text-secondary text-decoration-none',
          )}
          disabled={!hasMetricsData}
          onClick={() => setActiveDataSet('metrics')}
          type="button"
        >
          <FormattedMessage id="quoteDetails.Tab.Metrics" />
        </button>

        <div className="ms-auto d-flex align-items-center">
          <button
            aria-label={formatMessage({
              id: 'form.FeesAndMetrics.copyCurrent',
              defaultMessage: 'Copy current fees or metrics',
            })}
            className="btn btn-flat-secondary btn-md btn-icon d-flex align-items-center justify-content-center"
            disabled={currentDataSet.length === 0}
            onClick={onCopyToClipboard}
            type="button"
          >
            <i aria-hidden="true" className="icon icon-outlined icon-sm">
              content_copy
            </i>
          </button>
          <button
            aria-label={formatMessage({
              id: 'form.FeesAndMetrics.expandModal',
              defaultMessage: 'Open fees and metrics modal',
            })}
            className="btn btn-flat-secondary btn-md btn-icon d-flex align-items-center justify-content-center"
            onClick={toggleModal}
            type="button"
          >
            <i aria-hidden="true" className="icon icon-outlined icon-sm">
              crop_free
            </i>
          </button>
          {isExpandable && (
            <button
              aria-label={formatMessage({
                id: isExpanded ? 'form.FeesAndMetrics.collapse' : 'form.FeesAndMetrics.expand',
                defaultMessage: isExpanded ? 'Collapse fees and metrics' : 'Expand fees and metrics',
              })}
              className="btn btn-flat-secondary btn-md btn-icon d-flex align-items-center justify-content-center"
              onClick={toggleExpanded}
              type="button"
            >
              <i aria-hidden="true" className="icon icon-outlined icon-sm">
                {isExpanded ? 'unfold_less' : 'unfold_more'}
              </i>
            </button>
          )}
        </div>
      </div>

      {isContentExpanded && (
        <div className="d-flex flex-column fs-6 px-2">
          {currentDataSet.map((item, index) => {
            const itemLabel = formatMessage({
              id: `table.header.row.${item.name}` as any,
              defaultMessage: item.name,
            });

            return (
              <div
                className={classNames('d-flex align-items-center justify-content-between gap-3 py-1', {
                  'border-top': true,
                })}
                key={`${activeDataSet}-${item.name}-${index}`}
              >
                <span className="min-width-0 flex-grow-1 text-secondary text-truncate" title={itemLabel}>
                  {itemLabel}
                </span>
                <span className="tabular-figures text-end text-nowrap text-primary">
                  <FormattedValue value={item.value} precision={currentPrecision} />
                </span>
              </div>
            );
          })}
        </div>
      )}

      {isModalOpen && (
        <Modal animation={false} backdrop="static" keyboard={false} onHide={toggleModal} show size="lg">
          <ModalHeader closeButton>
            <ModalTitle as="h3">
              <FormattedMessage id="form.FeesAndMetrics.title" defaultMessage="Fees & metrics" />
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <FeesAndMetricsModalContent formId={tabId} />
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}

function getQuoteFeesFromDetails(fees: readonly Fee[] | undefined) {
  return fees?.filter(fee => !fee.name.includes('http'));
}

FeesAndMetricsField.displayName = 'FeesAndMetricsField';
